// @ts-nocheck

import { getGridFilters, getGridMetadata } from "models/dynamicGrid/selector";
import { createComponent } from "models/tableColumns/gridComponents";
import AgGridSearch from "organisms/searchFilter/agGridSearch";
import { getState } from "reducers";
import { getAllowedAggFuncs } from "./queries";
import NumericEditor from "models/tableColumns/numericEditor";
import CustomFloatingFilter from "organisms/searchFilter/components/customFloatingFilter";

const KEY_UP = 38;
const KEY_DOWN = 40;
const KEY_LEFT = 37;
const KEY_RIGHT = 39;

export const getChildTableColumnDefinitions = (
  tableColumns,
  tableId,
  cellRendererFramework,
  cellRendererParams
) => {
  const rowError = localStorage.getItem("rowUpdateError");
  const rowSuccess = localStorage.getItem("rowUpdateSuccess");

  return _.map(tableColumns, (t, index) => {
    const childTableInfo = {
      field: t.AppColumnName,
      headerName: t.AppColumnAliasName || t.AppColumn,
      width: Number(t.ColumnWidth),
      valueGetter: (params) => {
        return params.data ? params.data[t.AppColumnName] : null;
      },
      valueSetter: (params) => {
        const newVal = params.newValue;
        const data = params.data;
        if (data[t.AppColumnName] !== newVal) {
          data[t.AppColumnName] = newVal;
          return true;
        }
        return false;
      },
      equals: (valueA, valueB) => {
        return JSON.stringify(valueA) === JSON.stringify(valueB);
      },
      metaData: { ...t, tableId },
      resizable: !t.IsFixedColumn,
      cellRenderer: (props) => {
        return createComponent(t.ComponentName, {
          ...props,
          rowIdentifier: t.AppColumnName,
          tableId,
          type: t.ColumnType,
          cellRendererFramework: cellRendererFramework,
          rowError,
          rowSuccess,
        });
      },
      sortable: t.IsSortingEnabled,
      sort: cellRendererParams?.sortModal?.find(
        (res) => res.colId === t.AppColumnQueryName
      )?.sort,
      // autoHeight: true,
      wrapText: true,
      pinned: t.IsFixedColumn && t.PinPreference,
      //TBD
      suppressMovable: true,
      hide: !t.IsColumnEnabled || t.IsInternalColumn,
      suppressColumnsToolPanel: t.IsInternalColumn,
      editable: (params) =>
        (Boolean(t.ComponentName === "RenderInput") &&
          !params.data?.IsReadOnly) ||
        (t.IsEditableColumn && t.ComponentName !== "RenderCheckbox"),
      type: t.IsEditableColumn ? "editableColumn" : null,
      suppressFillHandle: !(
        t.ComponentName === "RenderInput" || t.IsEditableColumn
      ),
    };
    if (
      Boolean(t.ComponentName === "RenderInput") ||
      (t.IsEditableColumn && t.ComponentName !== "RenderCheckbox")
    ) {
      childTableInfo.cellEditor = NumericEditor;
    }
    return childTableInfo;
  });
};

export const getColumnDefinitions = (
  tableColumns,
  tableId,
  colDefs,
  refreshTimestamp,
  {
    defaultRowGroupColumn,
    defaultRowGroupColumnDisplayOverride,
    isPivotViewEnabled,
    isPivotMode,
    isUploadGrid,
    isMutliSelectFilterEnabled,
  },
  gridAutoSizeRef
) => {
  const gridFilters = getGridFilters(getState(), tableId);
  const gridMetaData = getGridMetadata(getState(), tableId);
  let updatedTableColumns = [...(tableColumns ?? [])].sort((a, b) => {
    const aIndex = _.findIndex(
      gridFilters?.rowGroupCols,
      (group) => a.AppColumnName === group.AliasName
    );
    const bIndex = _.findIndex(
      gridFilters?.rowGroupCols,
      (group) => b.AppColumnName === group.AliasName
    );
    if (aIndex >= 0 || bIndex >= 0) {
      return aIndex - bIndex;
    } else {
      return a.DefaultSortOrder < b.DefaultSortOrder ? -1 : 1;
    }
  });

  const findSort = (colData) => {
    return gridFilters?.multiSortColumns?.find(
      ({ sortField }) =>
        sortField === colData.AppColumnName ||
        sortField === colData.AppColumnQueryName
    )?.sortDirection;
  };
  const widthAutoSizeActive = gridAutoSizeRef?.current || false;
  const isAutoSized = Boolean(widthAutoSizeActive);
  const createColumnDefinition = (
    colData,
    index,
    sort,
    isTopLevel = true,
    parentHidden = false // new param for parent's hide
  ) => {
    let hasDefaultRowGroup;
    let cellRenderer;

    const widthValue = isAutoSized
      ? undefined
      : Number(colData.ColumnWidth) ?? 150;
    const rowGroupColumn =
      colData.AppColumnName === defaultRowGroupColumn
        ? colData
        : gridFilters?.rowGroupCols?.find(
            ({ AliasName }) => AliasName === colData.AppColumnName
          );

    const groupAggregationColumn = gridFilters?.groupAggregation?.find(
      ({ AliasName }) => AliasName === colData.AppColumnName
    );

    const pivotColumn = gridFilters?.pivotColumns?.find(
      ({ AliasName }) => AliasName === colData.AppColumnName
    );

    const defaultAggFunction = groupAggregationColumn
      ? groupAggregationColumn.QueryName?.split("(")[0]
      : null;

    const group = colDefs?.find(
      (c) => c.metaData.AppColumn === colData.AppColumn
    )?.rowGroup;

    const rowGroupIndex =
      rowGroupColumn &&
      Object.values(rowGroupColumn).find((r) => r === colData.AppColumnName)
        ? index
        : undefined;

    if (defaultRowGroupColumn) {
      hasDefaultRowGroup = rowGroupColumn ? true : group;
      cellRenderer = hasDefaultRowGroup && <span class="ag-group-cell"></span>;
    } else if (rowGroupColumn && refreshTimestamp === null) {
      hasDefaultRowGroup = true;
      cellRenderer = <span class="ag-group-cell"></span>;
    } else {
      hasDefaultRowGroup = group;
      cellRenderer = group && <span class="ag-group-cell"></span>;
    }

    const childCellGroupIndex = updatedTableColumns.findIndex(
      (col) => col.IsColumnEnabled && !col.IsInternalColumn
    );

    // base hide calculation for top-level
    const baseHide =
      hasDefaultRowGroup ||
      !colData.IsColumnEnabled ||
      colData.IsInternalColumn;

    // If not top-level, inherit hide from parent
    const hide = isTopLevel ? baseHide : parentHidden;
    const column = {
      field: colData.AppColumnName,
      headerName: colData.AppColumnAliasName || colData.AppColumn,
      headerTooltip: colData.AppColumnAliasName || colData.AppColumn,
      width: widthValue,
      metaData: { ...colData, tableId },
      resizable: !colData.IsFixedColumn,
      suppressAutoSize: colData.IsFixedColumn,
      floatingFilter: true,
      floatingFilterComponent: CustomFloatingFilter,
      floatingFilterComponentParams: {
        type: colData.ColumnType === "qty" ? "number" : "text",
        colDef: colData,
      },
      filter:
        !colData.IsInternalColumn && isTopLevel
          ? {
              component: AgGridSearch,
              // doesFilterPass: doesFilterPass,
            }
          : false,
      aggFunc: defaultAggFunction,
      suppressKeyboardEvent: function (params) {
        var event = params.event;
        var keyCode = event.keyCode;

        var isArrowKey =
          keyCode === KEY_UP ||
          keyCode === KEY_DOWN ||
          keyCode === KEY_LEFT ||
          keyCode === KEY_RIGHT;

        // ✅ Special handling only for tableId === 115
        if (
          tableId === 115 &&
          colData.ComponentName === "RenderInput" &&
          params.editing &&
          isArrowKey
        ) {
          event.preventDefault(); // stop value increment/decrement
          event.stopPropagation(); // stop editor + grid default handling
          return true; // suppress keyboard event
        }
         var gridShouldDoNothing = isArrowKey;
        return colData.ComponentName === "RenderInput" && gridShouldDoNothing;
      },

      cellRenderer:
        gridMetaData?.ChildTableID && index === childCellGroupIndex
          ? "agGroupCellRenderer"
          : (props) => {
              return createComponent(colData.ComponentName, {
                ...props,
                rowIdentifier: colData.AppColumnName,
                tableId,
                type: colData.ColumnType,
                defaultRowGroupColumn: hasDefaultRowGroup,
                defaultRowGroupColumnDisplayOverride,
                isPivotMode,
                isUploadGrid: isUploadGrid,
              });
            },
      cellRendererParams: gridMetaData?.ChildTableID &&
        index === childCellGroupIndex && {
          innerRenderer: (props) => {
            return createComponent(colData.ComponentName, {
              ...props,
              rowIdentifier: colData.AppColumnName,
              tableId,
              type: colData.ColumnType,
              defaultRowGroupColumn,
              defaultRowGroupColumnDisplayOverride,
              isPivotMode,
              isUploadGrid: isUploadGrid,
            });
          },
        },
      sortable: colData?.IsSortingEnabled ?? false,
      wrapText: true,
      rowGroup: tableId === 115 ? false : hasDefaultRowGroup,
      enablePivot: tableId === 115 ? false : isPivotViewEnabled,
      enableRowGroup: tableId === 115 ? false : colData.IsGroupingEnabled,
      enableValue: colData.IsGroupingEnabled,
      allowedAggFuncs:
        colData.Aggregation || getAllowedAggFuncs(colData.ColumnType),
      editable: (params) =>
        (Boolean(colData.ComponentName === "RenderInput") &&
          !params.data?.IsReadOnly) ||
        colData.IsEditableColumn,
      type: colData.IsEditableColumn ? "editableColumn" : null,
      suppressFillHandle: !(
        colData.ComponentName === "RenderInput" || colData.IsEditableColumn
      ),
      suppressMovable: tableId === 115 ? true : isPivotMode,
      pivot: tableId === 115 ? false : Boolean(pivotColumn),
      pinned: colData.IsFixedColumn && colData.PinPreference,
      hide, // now based on parent if child
      suppressColumnsToolPanel: colData.IsInternalColumn,
    };

    if (
      Boolean(colData.ComponentName === "RenderInput") ||
      colData.IsEditableColumn
    ) {
      column.cellEditor = NumericEditor;
    }

    if (colData.children) {
      return {
        ...column,
        children: colData.children.map((child, childIndex) =>
          createColumnDefinition(
            child,
            childIndex,
            undefined,
            false, // not top-level
            hide // pass parent's hide state down,
          )
        ),
      };
    } else {
      return sort ? { ...column, sort } : column;
    }
  };

  const columnDefinitions = _.map(updatedTableColumns, (col, index) =>
    createColumnDefinition(col, index, findSort(col), true)
  ); // Ensure top-level columns keep filters

  const filteredColumnDefinitions = columnDefinitions.sort((a, b) => {
    if (a.children && !b.children) return 1;
    if (!a.children && b.children) return -1;
    return 0;
  });

  if (tableId === 115) {
    return filteredColumnDefinitions.map((res) => {
      if (res.field === "Store" && !res.children) {
        // Case 1: No children → hide the column
        return {
          ...res,
          width: 0,
          minWidth: 0,
          maxWidth: 0,
          resizable: true,
          suppressSizeToFit: true,
          cellStyle: { padding: 0 },
          headerClass: "hidden-header",
        };
      }

      if (res.field === "Store" && Array.isArray(res.children)) {
        // Case 2: Has children → hide grandchildren
        return {
          ...res,
          hide: false,
          suppressColumnsToolPanel: true,
        };
      }

      // Default: return as-is
      return res;
    });
  } else {
    return filteredColumnDefinitions;
  }
};
